import {
  Authenticator,
  Button,
  View,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import * as Sentry from "@sentry/browser";
import { COLORS, V2Heading } from "@stacklet/ui";
import { Auth } from "aws-amplify";
import { useDeepCompareEffect } from "use-deep-compare";

import { clearLocalIdpUser } from "app/utils/auth";
import { SamlProvidersArraySchema } from "app/utils/configs";
import { localDevEnabled } from "app/utils/localDev";
import { storeRedirectReturnCookie } from "app/utils/redirection";
import StackletLogo from "assets/images/logo_vertical_color_1.svg?react";
import { RelayEnvironment } from "RelayEnvironment";

import { LocalIdpLogin } from "./LocalIdpLogin";
import "./Login.css";
import type { SamlProvider } from "app/utils/configs";
import type { CognitoConfigRecord } from "bootstrap";

export function fedSignIn(id: string, cognitoConfig: CognitoConfigRecord) {
  window.location.assign(
    `https://${cognitoConfig?.cognitoInstall}/oauth2/authorize?response_type=token&client_id=${cognitoConfig?.userPoolWebClientId}&idp_identifier=${id}&redirect_uri=${window.origin}&scope=email+openid+aws.cognito.signin.user.admin+stacklet/platform+stacklet/assetdb`,
  );
}

function renderSamlButtonsWithFooter(
  samlButtons: React.ReactNode[],
  toResetPassword: () => void,
) {
  return (
    <View textAlign="center">
      <div className="flex flex-col items-center gap-lg">{samlButtons}</div>
      <Button
        fontWeight="normal"
        onClick={toResetPassword}
        size="small"
        variation="link"
      >
        Reset Password
      </Button>
    </View>
  );
}

const authenticatorComponents = {
  Header() {
    return (
      <div className="mb-2xl flex flex-col items-center justify-center gap-xs">
        <StackletLogo className="stacklet-logo" height="64" width="64" />
        <V2Heading className="font-normal" level={2}>
          Sign In
        </V2Heading>
      </div>
    );
  },
  SignIn: {
    Footer() {
      const { toResetPassword } = useAuthenticator();

      const cognitoConfig: CognitoConfigRecord = RelayEnvironment.getStore()
        .getSource()
        .get("Cognito");

      // Try new saml_providers array first
      if (typeof cognitoConfig?.samlProviders === "string") {
        try {
          const parsed = JSON.parse(cognitoConfig.samlProviders);
          const result = SamlProvidersArraySchema.safeParse(parsed);

          if (result.success && result.data.length > 0) {
            const samlProviders = result.data;
            const buttons = samlProviders.map((provider: SamlProvider) => {
              return (
                <Button
                  data-testid={provider.idp_id}
                  key={provider.idp_id}
                  onClick={() => fedSignIn(provider.idp_id, cognitoConfig)}
                  slot="federated-buttons"
                  style={{
                    backgroundColor: COLORS.orange.base,
                    color: "white",
                    minWidth: "10rem",
                  }}
                >
                  {provider.display_name || provider.name}
                </Button>
              );
            });
            return renderSamlButtonsWithFooter(buttons, toResetPassword);
          } else if (!result.success) {
            // Validation failed
            console.error(
              "SAML providers validation failed:",
              result.error.errors,
            );
            Sentry.captureException(
              new Error("SAML providers validation failed"),
              {
                extra: {
                  validationErrors: result.error.errors,
                  samlProviders: cognitoConfig.samlProviders,
                },
              },
            );
          }
        } catch (error) {
          console.error("Failed to parse SAML providers configuration:", error);
          Sentry.captureException(
            new Error("Failed to parse SAML providers configuration"),
            {
              extra: { error, samlProviders: cognitoConfig.samlProviders },
            },
          );
          // Fall through to legacy saml dict or default rendering
        }
      }

      // Fallback to legacy saml dict for backward compatibility
      if (typeof cognitoConfig?.saml === "string") {
        try {
          const samlTargets = JSON.parse(cognitoConfig.saml);

          const buttons = Object.keys(samlTargets).map((d, key) => {
            return (
              <Button
                data-testid={key}
                key={key}
                onClick={() => fedSignIn(d, cognitoConfig)}
                slot="federated-buttons"
                style={{
                  backgroundColor: COLORS.orange.base,
                  color: "white",
                  minWidth: "10rem",
                }}
              >
                {samlTargets[d]}
              </Button>
            );
          });
          return renderSamlButtonsWithFooter(buttons, toResetPassword);
        } catch (error) {
          console.error("Failed to parse legacy SAML configuration:", error);
          Sentry.captureException(
            new Error("Failed to parse legacy SAML configuration"),
            {
              extra: { error, saml: cognitoConfig.saml },
            },
          );
          // Fall through to default rendering
        }
      } else {
        return (
          <View textAlign="center">
            <Button
              fontWeight="normal"
              onClick={toResetPassword}
              size="small"
              variation="link"
            >
              Reset Password
            </Button>
          </View>
        );
      }
      // oxlint-disable-next-line react/jsx-no-useless-fragment
      return <></>; // amplify types don't allow null here.
    },
  },
};

/** Props for the Login component */
interface Props {
  /** Children to render when authenticated */
  children: React.ReactNode;
}

export function Login({ children }: Props) {
  const { route, error } = useAuthenticator((context) => [
    context.route,
    context.error,
  ]);
  const searchParams = new URLSearchParams(window.location.search);

  useDeepCompareEffect(() => {
    if (error) {
      const errorMessage =
        typeof error === "string" ? error : "An authentication error occurred";
      console.error("Authentication error:", error);
      Sentry.captureException(
        new Error(`Authentication error: ${errorMessage}`),
      );
    }
  }, [error]);

  // Local-development backends (platform's `just local-console`,
  // VITE_DISABLE_AUTH=true): there's no Cognito, so swap the Amplify sign-in
  // for a trivial username form whose choice drives the local IdP (see
  // LocalIdpLogin / getAuthToken).  The DEV guard keeps this out of prod
  // builds even if the env var leaks into one.
  if (localDevEnabled()) {
    if (searchParams.has("logout")) {
      clearLocalIdpUser();
      window.location.assign(window.location.origin);
      return null;
    }
    return <LocalIdpLogin>{children}</LocalIdpLogin>;
  }

  if (searchParams.has("logout")) {
    Auth.signOut().then(() => window.location.assign(window.location.origin));
    return null;
  }

  if (route === "authenticated") {
    return children;
  }

  if (route === "signIn") {
    // We're about to render the signIn form - store the current URL so we can
    // redirect to it after the signIn event.
    storeRedirectReturnCookie();
  }

  return (
    <div
      className="grid grid-cols-[1.5fr_2fr]"
      style={
        {
          "--amplify-components-button-primary-background-color":
            COLORS.orange.base,
          "--amplify-components-button-primary-hover-background-color":
            COLORS.orange.L30,
        } as React.CSSProperties
      }
    >
      <Authenticator components={authenticatorComponents} hideSignUp>
        {children}
      </Authenticator>
      {/* Brand accent panel — stays a fixed dark navy in both themes
          (bg-legacy-navy-* mirrors to light in dark mode). */}
      <div className="bg-neutral-925" />
    </div>
  );
}
